/**
 * Account: send play GBUX to another player by username.
 * Server D1 debit/credit · Phantom message signature · mainnet product only.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useWalletState } from "@/contexts/wallet-context";
import { useDappSign } from "@/contexts/dapp-sign-context";
import {
  lookupPlayUser,
  fetchTransferChallenge,
  transferPlayGbuxByUsername,
} from "@/lib/play-transfer-api";
import {
  isLoggedIn,
  loginWithGrudgeId,
} from "@/lib/grudge-auth";
import { formatPlayGbux } from "@shared/gbux-math";
import { ArrowRightLeft, Loader2, Search, ShieldCheck } from "lucide-react";

export function PlayTransferPanel({
  playGbux,
  onDone,
}: {
  playGbux: number;
  onDone?: () => void;
}) {
  const { wallet, getConnectedWallet, refreshBalances } = useWalletState();
  const { requestSign } = useDappSign();
  const { toast } = useToast();
  const [toUser, setToUser] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [lookupHint, setLookupHint] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);
  const [sending, setSending] = useState(false);

  const pk = wallet.publicKey;
  const canSign = Boolean(getConnectedWallet());

  const onLookup = async () => {
    setLooking(true);
    setLookupHint(null);
    try {
      const r = await lookupPlayUser(toUser);
      if (!r.success) {
        setLookupHint(r.error || "Not found");
        return;
      }
      setToUser(r.username || toUser);
      setLookupHint(
        `Found @${r.username} · wallet ${r.walletHint}${
          (r.matches ?? 1) > 1 ? " · multiple matches — most recent used" : ""
        }`,
      );
    } catch (e) {
      setLookupHint(e instanceof Error ? e.message : "Lookup failed");
    } finally {
      setLooking(false);
    }
  };

  const onSend = async () => {
    if (!pk) {
      toast({
        title: "Connect wallet",
        description: "Phantom (or other Solana wallet) required to send play GBUX.",
        variant: "destructive",
      });
      return;
    }
    const amt = Math.floor(Number(amount));
    if (!toUser.trim() || amt < 1) {
      toast({
        title: "Check fields",
        description: "Enter recipient username and amount ≥ 1.",
        variant: "destructive",
      });
      return;
    }
    if (amt > playGbux) {
      toast({
        title: "Insufficient play GBUX",
        description: `You have ${formatPlayGbux(playGbux)}.`,
        variant: "destructive",
      });
      return;
    }

    setSending(true);
    try {
      const challenge = await fetchTransferChallenge({
        fromWallet: pk,
        toUsername: toUser,
        amount: amt,
      });
      const signed = await requestSign({
        purpose: "p2p_transfer",
        message: challenge.message,
        title: "Send play GBUX",
        description: "Transfer real play-ledger GBUX to another player.",
        details: [
          { label: "To", value: `@${toUser.replace(/^@/, "")}` },
          { label: "Amount", value: `${amt.toLocaleString()} GBUX` },
          { label: "From", value: `${pk.slice(0, 4)}…${pk.slice(-4)}` },
        ],
      });
      const result = await transferPlayGbuxByUsername({
        wallet: signed.wallet,
        toUsername: toUser,
        amount: amt,
        note: note || undefined,
        signed: {
          message: signed.message,
          signature: signed.signature,
        },
      });
      if (!result.success) {
        throw new Error(result.error || "Transfer failed");
      }
      toast({
        title: "Sent",
        description:
          result.message ||
          `Sent ${amt} play GBUX to @${toUser.replace(/^@/, "")}`,
      });
      setAmount("");
      setNote("");
      setLookupHint(null);
      void refreshBalances();
      onDone?.();
    } catch (e) {
      toast({
        title: "Transfer failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-amber-100/90 flex items-center gap-2">
          <ArrowRightLeft className="h-4 w-4 text-emerald-400" />
          Send play GBUX
        </h2>
        <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
          <strong className="text-emerald-300/90">Server wallet → server wallet only</strong>
          {" "}— real play-ledger GBUX between players on this app (by username).
          Not an on-chain SPL send. To leave the app (cash out / swap to chain), use{" "}
          <strong className="text-amber-200/80">Account → Help</strong> → email{" "}
          <a
            className="text-amber-300 underline"
            href="mailto:games@grudgestudio.org"
          >
            games@grudgestudio.org
          </a>
          .
        </p>
      </div>

      <div className="rounded-xl border border-emerald-500/20 bg-emerald-950/20 px-3 py-2 text-sm">
        <span className="text-zinc-500">Available </span>
        <strong className="text-emerald-300 tabular-nums">
          {formatPlayGbux(playGbux)}
        </strong>
      </div>

      {!pk && (
        <p className="text-sm text-amber-200/80 rounded-lg border border-amber-500/25 bg-amber-950/30 p-3">
          Connect a Solana wallet on the Wallet tab first.
        </p>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="p2p-user" className="text-amber-50/90 text-sm">
          Recipient username
        </Label>
        <div className="flex gap-2">
          <Input
            id="p2p-user"
            value={toUser}
            onChange={(e) => {
              setToUser(e.target.value);
              setLookupHint(null);
            }}
            placeholder="@playername"
            maxLength={32}
            className="bg-black/40 border-zinc-700 text-amber-50"
            autoComplete="off"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="shrink-0 border-zinc-700"
            disabled={looking || !toUser.trim()}
            onClick={() => void onLookup()}
            aria-label="Lookup username"
          >
            {looking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </div>
        {lookupHint && (
          <p
            className={`text-[11px] ${
              lookupHint.startsWith("Found")
                ? "text-emerald-400/90"
                : "text-rose-300/90"
            }`}
          >
            {lookupHint}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="p2p-amt" className="text-amber-50/90 text-sm">
          Amount (play GBUX)
        </Label>
        <Input
          id="p2p-amt"
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="100"
          className="bg-black/40 border-zinc-700 text-amber-50"
        />
        <div className="flex flex-wrap gap-1.5">
          {[100, 500, 1000, Math.floor(playGbux)].filter((n, i, a) => n > 0 && a.indexOf(n) === i).map((n) => (
            <button
              key={n}
              type="button"
              className="text-[10px] px-2 py-0.5 rounded-full border border-zinc-700 text-zinc-400 hover:border-amber-500/40 hover:text-amber-100"
              onClick={() => setAmount(String(n))}
            >
              {n >= playGbux && playGbux > 0 ? "Max" : n.toLocaleString()}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="p2p-note" className="text-amber-50/90 text-sm">
          Note <span className="text-zinc-500 font-normal">(optional)</span>
        </Label>
        <Input
          id="p2p-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Thanks for the game"
          maxLength={160}
          className="bg-black/40 border-zinc-700 text-amber-50"
        />
      </div>

      <div className="rounded-lg border border-zinc-800 bg-black/30 p-3 space-y-2 text-[11px] text-zinc-500 leading-snug">
        <p>
          {canSign ? (
            <span className="text-emerald-400/90">Signing ready</span>
          ) : (
            <span className="text-amber-300/80">Connect wallet to sign</span>
          )}{" "}
          · Phantom approves a message (not an on-chain tx) · debits play ledger only
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-[11px] border-violet-500/40 text-violet-200"
            onClick={() => loginWithGrudgeId("/account")}
          >
            <ShieldCheck className="h-3 w-3 mr-1" />
            Grudge ID
            {isLoggedIn() && (
              <span className="ml-1 text-emerald-400">· linked</span>
            )}
          </Button>
          <span className="text-zinc-600">
            Optional identity for fleet bag · transfer uses connected wallet
          </span>
        </div>
      </div>

      <Button
        type="button"
        className="w-full bg-emerald-700 hover:bg-emerald-600 text-white font-semibold"
        disabled={sending || !pk || !toUser.trim() || !amount}
        onClick={() => void onSend()}
      >
        {sending ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Approve in wallet…
          </>
        ) : (
          <>
            <ArrowRightLeft className="h-4 w-4 mr-2" />
            Send play GBUX
          </>
        )}
      </Button>
    </div>
  );
}
